import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { at } from 'lodash';
import { getIn, useField, useFormikContext, setIn } from 'formik';
import { Box, Button, Checkbox, FormControl, FormHelperText, MenuItem, Select, Stack, TextField, Typography, ListSubheader } from '@mui/material';
import { useSelector } from 'react-redux';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CustomButton from '../../../components/@extended/CustomButton';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function MultiSelectField({
  label,
  data = [],
  fieldKey,
  valueKey,
  enableOther = false,
  requiredDepends,
  requiredValidationValue,
  required,
  requiredDependOnFields,
  name,
  disabled: disabledProp,
  disabledDependsOnFields,
  disabledDepends,
  disabledValidationValue,
  justifycontent,
  ...rest
}) {
  // Reconstruct props for useField
  const props = { name, ...rest };
  const [field, meta] = useField(props);
  const { values, setFieldValue } = useFormikContext();
  const { value: selectedValue } = field;
  const [touched, error] = at(meta, 'touched', 'error');
  const isError = touched && error && true;
  const [disabled, setDisabled] = useState(false);
  const [toggleForOtherOption, setToggleForOtherOption] = useState(false);
  const [otherOption, setOtherOption] = useState('');
  const [localData, setLocalData] = useState(data);

  const isGrouped = useMemo(() => {
    if (!Array.isArray(localData) || localData.length === 0) return false;
    // Check if any item has type: "group_label"
    return localData.some((item) => item?.type === 'group_label');
  }, [localData]);

  // Replace the flatOptions useMemo:
  const flatOptions = useMemo(() => {
    if (!Array.isArray(localData)) return [];
    // Check if data is strings or objects
    const isStringFormat = localData.length > 0 && typeof localData[0] === 'string';
    if (isStringFormat) {
      return localData;
    }
    // Filter out group labels and extract values from objects
    return localData.filter((item) => item?.type !== 'group_label' && item?.value).map((item) => item.value);
  }, [localData]);

  // Add helper function for getting option label:
  const getOptionLabel = (item) => {
    return item?.label || item?.value || item;
  };

  useEffect(() => {
    if (disabledProp) {
      setDisabled(true);
    }
  }, [disabledProp]);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const selectedValues = useMemo(() => {
    return getIn(values, name) || [];
  }, [values, name]);

  // Add the missing onChange handler
  const handleSelectChange = (event) => {
    const value = event.target.value;
    // Ensure we always work with an array
    const newValue = Array.isArray(value) ? value : [];
    setFieldValue(name, newValue);
  };

  // Update the handleSubmitOtherOption function to work with new format:
  const handleSubmitOtherOption = () => {
    const trimmed = otherOption.trim();
    if (!trimmed) return;

    // Check for duplicates (case-insensitive)
    const lowerTrimmed = trimmed.toLowerCase();
    const alreadyExists = flatOptions.some((value) => value && value.toLowerCase() === lowerTrimmed);

    if (alreadyExists) {
      // Find the existing option with correct case and select it
      const existingValue = flatOptions.find((value) => value && value.toLowerCase() === lowerTrimmed);
      const currentValues = selectedValues || [];
      if (!currentValues.includes(existingValue)) {
        setFieldValue(name, [...currentValues, existingValue]);
      }
      setOtherOption('');
      setToggleForOtherOption(false);
      return;
    }

    // Check if the original data format is strings or objects
    const isStringFormat = Array.isArray(localData) && localData.length > 0 && typeof localData[0] === 'string';

    if (isStringFormat) {
      // Add as string to match the original format
      const newData = [...(Array.isArray(localData) ? localData : []), trimmed];
      setLocalData(newData);
    } else {
      // Add as object (for grouped or object format)
      const newOption = { value: trimmed, label: trimmed };
      const newData = [...(Array.isArray(localData) ? localData : []), newOption];
      setLocalData(newData);
    }

    // Add to selected values
    const currentValues = selectedValues || [];
    setFieldValue(name, [...currentValues, trimmed]);

    // Reset other option state
    setOtherOption('');
    setToggleForOtherOption(false);
  };

  const handleCancelOtherOption = () => {
    setOtherOption('');
    setToggleForOtherOption(false);
  };

  const handleOtherOptionChange = (e) => {
    e.stopPropagation();
    setOtherOption(e.target.value);
  };

  const handleOtherOptionKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitOtherOption();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelOtherOption();
    }
  };

  const isRequired = useMemo(() => {
    if (Array.isArray(requiredDependOnFields) && requiredDependOnFields.length > 0) {
      for (const dep of requiredDependOnFields) {
        if (dep.requiredDepends) {
          const dependsKey = dep.requiredDepends.includes('.') ? dep.requiredDepends.split('.') : dep.requiredDepends;
          const dependsValue = dep.requiredDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          
          // Check if requiredValidationValue is provided - if so, use exact match validation
          // If not provided, check if any value exists (truthy check)
          if (dep.requiredValidationValue !== undefined && dep.requiredValidationValue !== null && dep.requiredValidationValue !== '') {
            if (dependsValue === dep.requiredValidationValue) {
              return true;
            }
          } else {
            // If requiredValidationValue is not provided, check if any value exists
            if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
              return true;
            }
          }
        }
      }
    } else if (requiredDepends) {
      const dependsKey = requiredDepends.includes('.') ? requiredDepends.split('.') : requiredDepends;
      const dependsValue = requiredDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];
      
      // Check if requiredValidationValue is provided - if so, use exact match validation
      // If not provided, check if any value exists (truthy check)
      if (requiredValidationValue !== undefined && requiredValidationValue !== null && requiredValidationValue !== '') {
        if (dependsValue === requiredValidationValue) {
          return true;
        }
      } else {
        // If requiredValidationValue is not provided, check if any value exists
        if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
          return true;
        }
      }
    }

    if (required) {
      return true;
    }

    return false;
  }, [values, requiredDepends, requiredValidationValue, required, requiredDependOnFields]);

  const isDisabled = useMemo(() => {
    if (disabledProp || disabled) {
      return true;
    }

    if (Array.isArray(disabledDependsOnFields) && disabledDependsOnFields.length > 0) {
      for (const dep of disabledDependsOnFields) {
        if (dep.disabledDepends && dep.disabledValidationValue !== undefined) {
          const dependsKey = dep.disabledDepends.includes('.') ? dep.disabledDepends.split('.') : dep.disabledDepends;
          const dependsValue = dep.disabledDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          if (dependsValue === dep.disabledValidationValue) {
            return true;
          }
        }
      }
    } else if (disabledDepends && disabledValidationValue !== undefined) {
      const dependsKey = disabledDepends.includes('.') ? disabledDepends.split('.') : disabledDepends;
      const dependsValue = disabledDepends?.includes('.')
        ? values[dependsKey[0]]?.[dependsKey[1]]
        : values[dependsKey];
      if (dependsValue === disabledValidationValue) {
        return true;
      }
    }
    return false;
  }, [values, disabledDepends, disabledValidationValue, disabledProp, disabled, disabledDependsOnFields]);

  function _renderHelperText() {
    if (isError) {
      return <FormHelperText>{error}</FormHelperText>;
    } else {
      return <FormHelperText> </FormHelperText>;
    }
  }

  return (
    <Box
      sx={{
        height: justifycontent ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: justifycontent ? 'start' : 'space-between',
      }}
    >
      <Typography sx={{ color: '#5b5b5b', fontWeight: 'medium' }} variant="subtitle1" gutterBottom>
        {label}
        {label && isRequired && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
      </Typography>
      <FormControl error={isError}>
        <Select
          displayEmpty
          multiple
          disabled={isDisabled}
          required={isRequired}
          {...field}
          value={selectedValues || []} 
          onChange={handleSelectChange}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) {
              return <em style={{ color: '#aaa', fontStyle: 'normal' }}>{label}</em>;
            }
            const isStringFormat = Array.isArray(localData) && localData.length > 0 && typeof localData[0] === 'string';
            if (isStringFormat) {
              // For string format, show all selected values (including dynamically added ones)
              return selected.join(', ');
            }
            // For object format, map values to labels
            const labels = selected.map((val) => {
              const originalItem = localData.find((item) => item?.value === val);
              return getOptionLabel(originalItem) || val;
            });
            return labels.join(', ');
          }}
        >
          <MenuItem value="" disabled>
            <em style={{ color: '#aaa', fontStyle: 'normal' }}>{label}</em>
          </MenuItem>
          {isGrouped
            ? Array.isArray(localData) &&
              localData.map((item, index) => {
                if (item?.type === 'group_label') {
                  return (
                    <ListSubheader key={`group-${index}`} disableSticky>
                      {item.label}
                    </ListSubheader>
                  );
                }
                if (item?.value) {
                  return (
                    <MenuItem key={`item-${index}`} value={item.value}>
                      <Checkbox
                        checked={selectedValues?.includes(item.value)}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                      />
                      {getOptionLabel(item)}
                    </MenuItem>
                  );
                }

                return null;
              })
            : Array.isArray(localData) &&
              localData.map((item, index) => {
                const optionValue = item?.value || item;
                const optionLabel = getOptionLabel(item);
                return (
                  <MenuItem key={index} value={optionValue}>
                    <Checkbox
                      checked={selectedValues?.includes(optionValue)}
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                    />
                    {optionLabel}
                  </MenuItem>
                );
              })}

          {enableOther && toggleForOtherOption ? (
            <Stack direction="row" spacing={1} sx={{ pl: 2, pr: 2, pt: 1, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter custom option..."
                value={otherOption}
                onChange={handleOtherOptionChange}
                onKeyDown={handleOtherOptionKeyDown}
                autoFocus
                error={
                  otherOption.trim() &&
                  flatOptions.some((value) => value && value.toLowerCase() === otherOption.trim().toLowerCase())
                }
                helperText={
                  otherOption.trim() &&
                  flatOptions.some((value) => value && value.toLowerCase() === otherOption.trim().toLowerCase())
                    ? 'This option already exists'
                    : ''
                }
              />
              <CustomButton
                sx={{ height: 39, minWidth: 80 }}
                variant="contained"
                onClick={handleSubmitOtherOption}
                disabled={!otherOption.trim()}
              >
                Add
              </CustomButton>
              <CustomButton sx={{ height: 39, minWidth: 80 }} variant="outlined" onClick={handleCancelOtherOption}>
                Cancel
              </CustomButton>
            </Stack>
          ) : enableOther ? (
            <MenuItem
              sx={{
                ':hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                justifyContent: 'flex-start',
                pl: 2,
                color: '#1976d2',
                fontSize: '14px',
                fontStyle: 'italic',
                borderTop: '1px solid #e0e0e0',
                mt: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setToggleForOtherOption(true);
              }}
            >
              + Other (Please specify)
            </MenuItem>
          ) : null}
        </Select>
        {_renderHelperText()}
      </FormControl>
    </Box>
  );
}

MultiSelectField.propTypes = {
  data: PropTypes.array.isRequired,
  enableOther: PropTypes.bool,
};

export default React.memo(MultiSelectField);